import {
  type BillingPeriod,
  type BusinessInvoice,
  type CreditPack,
  type PaidSubscriptionTier,
  type SubscriptionTier,
  toSubscriptionTier,
} from '@pulsify/shared/dto'
import { api, unwrap } from '~/shared/lib/rpc'

export type { SubscriptionTier }
export type SubscriptionStatusValue = 'Free' | 'Active' | 'Inactive' | 'Trial'

export interface PendingChange {
  effectiveAt: string
  targetTier: SubscriptionTier
  targetBillingPeriod: BillingPeriod | null
}

export interface SubscriptionLimits {
  maxActiveEvents: number
  maxDraftEvents: number
  maxAdvancePublishDays: number
}

export interface SubscriptionUsage {
  activeEvents: number
  draftEvents: number
  listings: number
}

interface SubscriptionStatus {
  /** Whose plan this is: the profile's own, or the organization's it falls back to. */
  source: 'profile' | 'organization'
  billingEntityId: string | null
  tier: SubscriptionTier
  status: SubscriptionStatusValue
  billingPeriod: BillingPeriod | null
  pendingChange: PendingChange | null
  trialEndsAt: string | null
  limits: SubscriptionLimits
  usage: SubscriptionUsage
}

export interface StripePrice {
  priceId: string
  unitAmount: number
  currency: string
  billingPeriod: BillingPeriod
}

export interface StripeProduct {
  type: string
  productName: string | null
  prices: StripePrice[]
}

interface PricingResponse {
  products: StripeProduct[]
  unavailable: { type: string; reason: string }[]
  creditPack?: CreditPack | null
}

export interface CreateCheckoutRequest {
  businessId: string
  priceId: string
  successUrl: string
  cancelUrl: string
  /** Buys the plan for this profile instead of the whole organization. */
  profileId?: string
  /** Who pays a new profile plan; the organization when left out. */
  billingEntityId?: string
}

interface CreateCheckoutResponse {
  sessionId: string
  url: string | null
}

interface TrialSubscriptionRequest {
  businessId: string
  durationDays: number
  productType: PaidSubscriptionTier
}

type BackendStatus = 'free' | 'active' | 'trialing' | 'payment_failed'

interface BackendSubscriptionStatus {
  tier: string
  status: BackendStatus
  billingPeriod: BillingPeriod | null
  pendingChange: {
    effectiveAt: string
    targetTier: string
    targetBillingPeriod: BillingPeriod | null
  } | null
  trialEndsAt: string | null
  limits: {
    maxActiveEvents: number
    maxDraftEvents: number
    maxAdvanceDays: number
    maxImages: number
  }
  usage: SubscriptionUsage
  source?: 'profile' | 'organization'
  billingEntityId?: string | null
}

const STATUS_MAP: Record<BackendStatus, SubscriptionStatusValue> = {
  free: 'Free',
  active: 'Active',
  trialing: 'Trial',
  payment_failed: 'Inactive',
}

function adaptStatus(input: BackendSubscriptionStatus): SubscriptionStatus {
  return {
    source: input.source ?? 'organization',
    billingEntityId: input.billingEntityId ?? null,
    tier: toSubscriptionTier(input.tier),
    status: STATUS_MAP[input.status],
    billingPeriod: input.billingPeriod,
    pendingChange: input.pendingChange
      ? {
          effectiveAt: input.pendingChange.effectiveAt,
          targetTier: toSubscriptionTier(input.pendingChange.targetTier),
          targetBillingPeriod: input.pendingChange.targetBillingPeriod,
        }
      : null,
    trialEndsAt: input.trialEndsAt,
    limits: {
      maxActiveEvents: input.limits.maxActiveEvents,
      maxDraftEvents: input.limits.maxDraftEvents,
      maxAdvancePublishDays: input.limits.maxAdvanceDays,
    },
    usage: input.usage,
  }
}

export const stripeApi = {
  async getSubscriptionStatus(businessId: string, profileId?: string): Promise<SubscriptionStatus> {
    const res = profileId
      ? await api.api.subscription.profiles[':profileId'].status.$get({
          param: { profileId },
          query: { businessId },
        })
      : await api.api.subscription.status[':businessId'].$get({ param: { businessId } })
    const body = await unwrap<BackendSubscriptionStatus>(res)
    return adaptStatus(body)
  },

  async getPricing(): Promise<PricingResponse> {
    const res = await api.api.subscription.pricing.$get()
    return unwrap<PricingResponse>(res)
  },

  async createCheckout(request: CreateCheckoutRequest): Promise<CreateCheckoutResponse> {
    const { profileId, billingEntityId, ...rest } = request
    const res = profileId
      ? await api.api.subscription.profiles[':profileId'].checkout.$post({
          param: { profileId },
          json: { ...rest, ...(billingEntityId ? { billingEntityId } : {}) },
        })
      : await api.api.subscription.checkout.$post({ json: rest })
    return unwrap<CreateCheckoutResponse>(res)
  },

  async getInvoices(businessId: string): Promise<BusinessInvoice[]> {
    const res = await api.api.subscription.invoices[':businessId'].$get({ param: { businessId } })
    const { invoices } = await unwrap<{ invoices: BusinessInvoice[] }>(res)
    return invoices
  },

  async syncPlans(businessId: string): Promise<void> {
    const res = await api.api.subscription.sync[':businessId'].$post({ param: { businessId } })
    await unwrap(res)
  },

  async createPortalSession(
    businessId: string,
    returnUrl: string,
    profileId?: string,
  ): Promise<{ url: string }> {
    const res = profileId
      ? await api.api.subscription.profiles[':profileId'].portal.$post({
          param: { profileId },
          query: { businessId },
          json: { returnUrl },
        })
      : await api.api.subscription.portal[':businessId'].$post({
          param: { businessId },
          json: { returnUrl },
        })
    return unwrap<{ url: string }>(res)
  },

  async cancelSubscription(businessId: string, profileId?: string): Promise<void> {
    const res = profileId
      ? await api.api.subscription.profiles[':profileId'].cancel.$post({
          param: { profileId },
          query: { businessId },
        })
      : await api.api.subscription.cancel[':businessId'].$post({ param: { businessId } })
    await unwrap<void>(res)
  },

  async resumeSubscription(businessId: string, profileId?: string): Promise<void> {
    const res = profileId
      ? await api.api.subscription.profiles[':profileId'].resume.$post({
          param: { profileId },
          query: { businessId },
        })
      : await api.api.subscription.resume[':businessId'].$post({ param: { businessId } })
    await unwrap<void>(res)
  },

  async trialSubscription(request: TrialSubscriptionRequest): Promise<void> {
    const res = await api.api.subscription.trial.$post({ json: request })
    await unwrap<void>(res)
  },
}
